import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  Tag,
  Tooltip,
  Button,
  Select,
  message,
  Modal,
  Form,
} from "antd";

import { FileOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { formatDateTime } from "../../helpers/date";
import {
  CARGO_ACTIVATION_STATUSES,
  CARGO_ACTIVATION_REJECT_REMARKS,
} from "../../constants/cargoActivation";

const CargoActivationList = ({ cargoActivations, loading }) => {
  const [updatingActivation, setUpdatingActivation] = useState(null);

  const view = async (cargoActivation) => {
    await axios
      .post(`/api/cargo-activations/${cargoActivation.objectId}/attachment`, {
        attachment: cargoActivation.attachment,
      })
      .then((response) => {
        window.open(response.data.data.url, "_blank").focus();
      })
      .catch((error) => {
        message.error(error.response?.data.message || error.message);
      });
  };

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize(); // check on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const table = [
    {
      title: "Cargo Activation Id",
      dataIndex: ["objectId"],
      width: 120,
      render: (ticketId) => <Tag color="blue">{ticketId}</Tag>,
    },
    {
      title: "Submitted At",
      dataIndex: "createdAt",
      width: 150,
      render: (createdAt) => formatDateTime(createdAt),
    },
    {
      title: "type",
      dataIndex: ["type"],
      width: 100,
    },
    {
      title: "Merchant",
      dataIndex: ["merchant", "user", "email"],
    },
    {
      title: "Registration Info",
      dataIndex: ["registrationName"],
      render: (registrationName, activation) => {
        if (!registrationName) {
          return "-";
        }

        return (
          <p>
            {registrationName}
            <br />({activation.registrationNumber || "-"})
          </p>
        );
      },
    },
    {
      title: "Status",
      dataIndex: ["status"],
      width: 110,
      render: (status, cargoActivation) => {
        const statusSpec = Object.values(CARGO_ACTIVATION_STATUSES).find(
          (activationStatus) => activationStatus.key === status
        );

        return (
          <>
            <Tag color={statusSpec?.color || "gray"}>
              {statusSpec?.name || status}
              {` `}
              {cargoActivation.remark && (
                <Tooltip title={`Remark: ${cargoActivation.remark}`}>
                  <InfoCircleOutlined />
                </Tooltip>
              )}
            </Tag>
          </>
        );
      },
    },
    {
      title: "",
      dataIndex: "action",
      width: 140,
      render: (_, record) => (
        <div>
          {record.status === CARGO_ACTIVATION_STATUSES.PENDING.key && (
            <>
              <Select
                defaultValue={"Select"}
                className="select-before"
                style={{ width: "100px" }}
                onChange={(value) =>
                  setUpdatingActivation({
                    cargoActivation: record,
                    status: value,
                  })
                }
              >
                <Select.Option
                  key={CARGO_ACTIVATION_STATUSES.APPROVED.key}
                  value={CARGO_ACTIVATION_STATUSES.APPROVED.key}
                >
                  {CARGO_ACTIVATION_STATUSES.APPROVED.name}
                </Select.Option>
                <Select.Option
                  key={CARGO_ACTIVATION_STATUSES.REJECTED.key}
                  value={CARGO_ACTIVATION_STATUSES.REJECTED.key}
                >
                  {CARGO_ACTIVATION_STATUSES.REJECTED.name}
                </Select.Option>
              </Select>
              <Modal
                title={`Set Activation by ${updatingActivation?.cargoActivation?.merchant?.user?.email} to ${updatingActivation?.status}?`}
                visible={!!updatingActivation}
                footer={null}
                onCancel={() => {
                  setUpdatingActivation(null);
                }}
              >
                <Form
                  name="updateStatusForm"
                  labelCol={{
                    span: 4,
                  }}
                  wrapperCol={{
                    span: 20,
                  }}
                  initialValues={{
                    remark: null,
                  }}
                  onFinish={async (values) => {
                    await axios
                      .post(
                        `/api/cargo-activations/${updatingActivation?.cargoActivation.objectId}`,
                        {
                          ...values,
                          status: updatingActivation?.status,
                        }
                      )
                      .then((_) => {
                        message.info(
                          `#${updatingActivation?.cargoActivation.objectId} succesfully updated`
                        );
                        setUpdatingActivation(null);
                        window.location.reload();
                      })
                      .catch((error) => {
                        message.error(
                          error.response?.data.message || error.message
                        );
                      });
                  }}
                  onFinishFailed={() => {}}
                >
                  {updatingActivation?.status ===
                    CARGO_ACTIVATION_STATUSES.REJECTED.key && (
                    <Form.Item
                      label="Remark"
                      name="remark"
                      rules={[
                        {
                          required:
                            updatingActivation?.status ===
                            CARGO_ACTIVATION_STATUSES.REJECTED.key,
                          message: "Please select rejection remark",
                        },
                      ]}
                    >
                      <Select
                        style={{ width: "100%" }}
                        options={CARGO_ACTIVATION_REJECT_REMARKS.map(
                          (remark) => ({
                            value: remark.remark,
                            label: remark.remark,
                          })
                        )}
                      />
                    </Form.Item>
                  )}
                  <Form.Item wrapperCol={{ offset: 4, span: 20 }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      style={{ marginRight: 8 }}
                    >
                      Submit
                    </Button>
                    <Button
                      onClick={() => {
                        setUpdatingActivation(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Form.Item>
                </Form>
              </Modal>
            </>
          )}
        </div>
      ),
    },
    {
      title: "",
      dataIndex: "action",
      fixed: isMobile ? undefined : "right",
      width: 80,
      render: (_, record) => (
        <Tooltip title="View Document">
          <Button
            type="primary"
            icon={<FileOutlined />}
            onClick={() => {
              view(record);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="table">
      <Table
        rowKey="objectId"
        className="table-section"
        columns={table}
        dataSource={cargoActivations}
        pagination={false}
        loading={loading}
        scroll={{ x: 1100, y: 600 }}
      />
    </div>
  );
};

export default CargoActivationList;
